<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { fetchTrayAbandonedCheckouts } from '../../api/tray';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const isLoading = ref(true);
const records = ref([]);
const hasError = ref(false);
const accountId = computed(() => Number(route.params.accountId));
const errorMessage = computed(() => t('TRAY_PAGES.ABANDONED.ERROR'));

const buildIdentityLine = checkout =>
  [checkout.customer_email || '—', checkout.customer_phone || '—'].join(' · ');

const loadRecords = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    const { data } = await fetchTrayAbandonedCheckouts(accountId.value);
    records.value = data.abandoned_checkouts || [];
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const openContact = contactId => {
  if (!contactId) return;
  router.push({
    name: 'contacts_edit',
    params: { accountId: accountId.value, contactId },
  });
};

onMounted(() => {
  loadRecords();
  store.dispatch('integrations/get', 'tray').catch(() => {});
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('TRAY_PAGES.ABANDONED.TITLE')"
        :description="$t('TRAY_PAGES.ABANDONED.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <div
          v-if="hasError"
          class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
        >
          {{ errorMessage }}
        </div>
        <div
          v-else-if="!records.length"
          class="rounded-md border border-dashed border-n-weak p-6 text-sm text-n-slate-11"
        >
          {{ $t('TRAY_PAGES.ABANDONED.EMPTY') }}
        </div>
        <div v-else class="grid gap-4">
          <div
            v-for="checkout in records"
            :key="checkout.id || checkout.session_id"
            class="rounded-md border border-n-weak bg-n-alpha-3 p-4"
          >
            <div
              class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
            >
              <div class="min-w-0 space-y-1">
                <p class="mb-0 text-base font-semibold text-n-slate-12">
                  {{
                    $t('TRAY_PAGES.ABANDONED.CHECKOUT_ID', {
                      id: checkout.id || checkout.session_id || '—',
                    })
                  }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{
                    checkout.customer_name ||
                    checkout.customer_email ||
                    checkout.customer_phone ||
                    '—'
                  }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ buildIdentityLine(checkout) }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ checkout.total || '—' }} {{ checkout.currency || '' }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  disabled
                  variant="outline"
                  color="slate"
                  :label="$t('TRAY_PAGES.OPEN_CHECKOUT')"
                />
                <Button
                  :disabled="!checkout.contact_id"
                  :label="$t('TRAY_PAGES.OPEN_CONTACT_TO_MESSAGE')"
                  @click="openContact(checkout.contact_id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
